import React, { useMemo } from "react";
import { Avatar, Text } from "react-native-paper";
import {
  Linking,
  Pressable,
  StyleSheet,
  TextStyle,
  View,
  ViewStyle,
} from "react-native";
import { useAppSelector } from "../redux/store";
import { useTranslation } from "react-i18next";
import {
  safeToString,
  getNestedValue,
  formatDateTimeNoYear,
} from "../utils/utils";

type Props = {
  field: any;
  value: any;
  collectionPickerContainerStyles?: ViewStyle;
  style?: ViewStyle | TextStyle;
};

const CONTACT_ICON_SIZE = 22;
const CONTACT_TEXT_SIZE = 12;
const CONTACT_ROW_GAP = 6;

export const ValueDisplay = (props: Props) => {
  const { field, value, collectionPickerContainerStyles, style } = props;
  const { t } = useTranslation();
  const theme = useAppSelector((state) => state.auth.theme);
  const userRole = useAppSelector((state) => state.auth.user.role);

  const rentalPeriodString = useMemo(() => {
    if (value?.startDate && value?.endDate) {
      const startDate = new Date(value.startDate.seconds * 1000);
      const endDate = new Date(value.endDate.seconds * 1000);
      return `${startDate.toLocaleString("ru-RU").split(",")[0]} - ${endDate.toLocaleString("ru-RU").split(",")[0]}`;
    }

    return "";
  }, [value?.startDate, value?.endDate]);

  const linkColor = theme === "light" ? "#1E40AF" : "#90d5ff";

  switch (field.type) {
    case "text":
    case "password":
    case "textarea":
    case "number":
    case "segmentedButton":
    case "dropdown":
    case "calculation":
    case "radio":
    case "numberId":
    case "status": {
      const rawValue = value ?? field.defaultValue;
      const normalizedValue =
        Array.isArray(field.options) &&
        rawValue !== null &&
        typeof rawValue !== "undefined" &&
        !field.options.includes(rawValue)
          ? field.defaultValue
          : rawValue;

      return (
        <Text variant={field.variant || "bodyLarge"} style={{ ...style }}>
          {typeof normalizedValue === "object" && normalizedValue !== null
            ? normalizedValue.name ||
              normalizedValue.id ||
              JSON.stringify(normalizedValue)
            : t(normalizedValue)}
        </Text>
      );
    }

    case "calculated":
      return (
        <Text variant="titleMedium" style={{ fontWeight: "600" }}>
          {typeof value === "object" && value !== null
            ? value.name || value.id || JSON.stringify(value)
            : t(value)}
        </Text>
      );

    case "dateRangePicker": {
      return <Text>{rentalPeriodString}</Text>;
    }

    case "booleanCheckbox": {
      if (field.booleanDisplayMode === "inlineText") {
        return (
          <Text variant={field.variant || "bodyLarge"} style={{ ...style }}>
            {value ? t("Yes") : t("No")}
          </Text>
        );
      }

      return (
        <Text variant={field.variant || "bodyLarge"} style={{ ...style }}>
          {String(value ?? "")}
        </Text>
      );
    }

    case "phone": {
      const phoneValue = String(value || "");
      const whatsappNumber = phoneValue.replace(/\D/g, "");

      if (!phoneValue) {
        return null;
      }

      return (
        <View style={styles.contactBlock}>
          <Pressable
            onPress={() => Linking.openURL(`tel:${phoneValue}`)}
            style={styles.contactActionRow}
          >
            <Avatar.Icon
              size={CONTACT_ICON_SIZE}
              icon="phone"
              color="#ffffff"
              style={[styles.contactActionIcon, { backgroundColor: "#16a34a" }]}
            />
            <Text style={styles.contactActionText}>{phoneValue}</Text>
          </Pressable>
          <Pressable
            onPress={() => Linking.openURL(`https://wa.me/${whatsappNumber}`)}
            style={styles.contactActionRow}
          >
            <Avatar.Icon
              size={CONTACT_ICON_SIZE}
              icon="whatsapp"
              color="#ffffff"
              style={[styles.contactActionIcon, { backgroundColor: "#22c55e" }]}
            />
            <Text style={styles.contactActionText}>
              {t("Write in WhatsApp")}
            </Text>
          </Pressable>
        </View>
      );
    }

    case "email":
    case "url": {
      const link = field.type === "url" ? "https://" : "mailto:";
      return (
        <Pressable onPress={() => Linking.openURL(link + value)}>
          <Text
            style={{
              color: linkColor,
              textDecorationLine: "underline",
            }}
          >
            {value}
          </Text>
        </Pressable>
      );
    }

    case "collectionPicker": {
      if (value) {
        let text = safeToString(getNestedValue(value, field.key));
        let text1 = safeToString(getNestedValue(value, field.secondKey));
        let text2 = safeToString(getNestedValue(value, field.thirdKey));
        const hideSecondKeyForRole =
          !!field.hideSecondKeyInDisplayForRoles?.includes(userRole);
        const showSecondKey =
          field.secondKey &&
          !hideSecondKeyForRole &&
          !field.hideSecondKeyInDisplay;
        return (
          <View style={collectionPickerContainerStyles}>
            <Text style={styles.titleMedium}>{text}</Text>
            {showSecondKey && <Text style={styles.subText}>{text1}</Text>}
            {field.thirdKey && <Text style={styles.subText}>{text2}</Text>}
          </View>
        );
      }
      return null;
    }
    case "dateTime":
      return (
        <Text style={{ fontWeight: "bold" }}>
          {value ? value.toDate().toLocaleString("ru-RU") : ""}
        </Text>
      );
    case "dateTimeNoYear":
      return (
        <Text style={{ fontWeight: "bold" }}>
          {formatDateTimeNoYear(value)}
        </Text>
      );
    case "datePicker":
      return (
        <Text style={{ fontWeight: "bold" }}>
          {value ? value.toDate().toLocaleString("ru-RU").split(",")[0] : ""}
        </Text>
      );
    case "warehouseItemsPicker": {
      // Handle object structure: { "itemId": { name: "...", quantity: N } }
      const warehouseItems = value || {};
      const warehouseItemsArray = Object.entries(warehouseItems).map(
        ([itemId, itemData]: [string, any]) => ({
          itemId,
          name: itemData?.name || itemId,
          quantity: itemData?.quantity || 0,
        }),
      );
      // Don't display if empty
      if (warehouseItemsArray.length === 0) {
        return null;
      }
      return (
        <Text style={{ fontWeight: "bold" }}>
          {warehouseItemsArray
            .map((item) => `${item.name} (×${item.quantity})`)
            .join(", ")}
        </Text>
      );
    }
    case "object": {
      return (
        <Text style={{ fontWeight: "bold" }}>
          {typeof value === "object" && value !== null
            ? value.name || value.id || JSON.stringify(value)
            : value}
        </Text>
      );
    }
    default: {
      return (
        <Text style={{ fontWeight: "bold" }}>
          {typeof value === "object" && value !== null
            ? value.name || value.id || JSON.stringify(value)
            : value}
        </Text>
      );
    }
  }
};

const styles = StyleSheet.create({
  titleMedium: {
    fontWeight: "bold",
  },
  subText: {
    color: "gray",
  },
  contactBlock: {
    borderRadius: 14,
    overflow: "hidden",
  },
  contactActionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: CONTACT_ROW_GAP,
    paddingVertical: 4,
  },
  contactActionDivider: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: "#374151",
    opacity: 0.6,
    marginLeft: CONTACT_ICON_SIZE + CONTACT_ROW_GAP,
  },
  contactActionIcon: {
    justifyContent: "center",
    alignItems: "center",
  },
  contactActionText: {
    fontSize: CONTACT_TEXT_SIZE,
    lineHeight: 24,
    fontWeight: "500",
  },
});
